import { FC } from 'react';

const AboutPage: FC = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-center">About DLDV</h1>
          <p className="text-xl text-center max-w-3xl mx-auto">
            Transforming financial service marketing through AI-powered solutions and industry expertise
          </p>
        </div>
      </section>

      {/* Company Overview Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <p className="mb-4 text-justify">
                DLDV was founded by Dirk de Vlaam with a clear vision: to revolutionize how financial service providers approach marketing and business development through the strategic application of AI and automation technologies.
              </p>
              <p className="mb-4 text-justify">
                Drawing from extensive experience in the financial sector, we identified a critical gap between traditional marketing approaches and the potential of modern technology. Financial service providers often struggle with inefficient processes, inconsistent messaging, and difficulty scaling their outreach efforts.
              </p>
              <p className="text-justify">
                Today, DLDV stands as a trusted partner for investment firms and financial service providers seeking to leverage cutting-edge technology while maintaining the personal touch that defines their brand identity.
              </p>
            </div>
            <div className="flex justify-center">
              <img src="/images/dldv.svg" alt="DLDV" className="w-full max-w-md" />
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                      <div className="order-2 md:order-1 flex justify-center">
              <div className="card p-6 w-full max-w-md">
                <img src="/images/ds2021-06-19-088b.jpg" alt="Dirk de Vlaam" className="w-full rounded-lg mb-4" />
                <h3 className="text-2xl font-bold mb-4">Dirk de Vlaam</h3>
                <p className="text-lg font-medium text-accent-color mb-2">Founder & CEO</p>
                <p className="mb-4 text-justify">
                  With a distinguished career spanning financial services and technology, Dirk brings a wealth of experience and a unique perspective to DLDV.
                </p>
                <a href="https://www.linkedin.com/in/dirkdevlaam/" target="_blank" rel="noopener noreferrer" className="flex items-center text-primary-color hover:text-accent-color transition">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                  Connect on LinkedIn
                </a>
              </div>
            </div>
            <div className="order-1 md:order-2 animate-fade-in">
              <h2 className="text-3xl font-bold mb-6">Leadership</h2>
              <p className="mb-4 text-justify">
                DLDV is led by Dirk de Vlaam, whose background combines deep financial industry knowledge with technological innovation expertise. This unique combination allows us to bridge the gap between traditional financial services and cutting-edge AI solutions.
              </p>
              <p className="mb-4 text-justify">
                Prior to founding DLDV, Dirk held various leadership positions in financial institutions where he witnessed firsthand the challenges and opportunities in marketing and business development for investment firms.
              </p>
              <p className="text-justify">
                His vision for DLDV emerged from recognizing that AI and automation could transform these challenges into competitive advantages, enabling financial service providers to achieve unprecedented efficiency and effectiveness in their marketing efforts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Mission & Values</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Mission</h3>
              <p className="text-justify">
                To empower financial service providers with AI-enhanced marketing solutions that drive growth, efficiency, and exceptional client experiences while maintaining the human touch that defines their brand.
              </p>
            </div>
            
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Vision</h3>
              <p className="text-justify">
                To be the leading innovator in AI-powered marketing solutions for the financial services industry, setting new standards for how investment firms connect with their clients and prospects.
              </p>
            </div>
            
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Values</h3>
              <p className="text-justify">
                Innovation, integrity, excellence, and client-centricity guide everything we do. We believe in transparent partnerships, continuous improvement, and delivering measurable results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Why Choose DLDV</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Industry Expertise</h3>
              <p className="text-justify">
                Our team combines decades of experience in financial services with cutting-edge technological knowledge, allowing us to understand your unique challenges and opportunities.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">AI-First Approach</h3>
              <p className="text-justify">
                We don't just add AI as an afterthought – our solutions are built from the ground up with artificial intelligence at their core, ensuring maximum efficiency and effectiveness.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Tailored Solutions</h3>
              <p className="text-justify">
                We recognize that every financial service provider has unique needs and challenges. Our solutions are customized to address your specific goals and integrate seamlessly with your existing processes.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Measurable Results</h3>
              <p className="text-justify">
                We're committed to delivering tangible outcomes. Our solutions include comprehensive analytics and reporting, allowing you to track ROI and continuously optimize your marketing efforts.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-primary">Get in Touch</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
